import { useEffect, useMemo, useRef } from "react";
import Highcharts from "highcharts";
import { HighchartsReact, HighchartsReactRefObject } from "highcharts-react-official";
import { faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { Button } from "@/components/shadcn/button";
import { Spinner } from "@/components/common/Loading/Spinner";
import { AiSectionHeading } from "./AiSectionHeading";

import type { TokenUsagePoint } from "../types";

const tokenFormatter = new Intl.NumberFormat(undefined);

// Aligned with the app's theme tokens (--border, --muted-foreground, --dark) so the chart uses
// colours consistent with the rest of the design system.
const CHART_COLORS = {
  line: "#2563eb",
  cumulativeLine: "#4f4db9",
  limitLine: "#f87171",
  gridline: "#d9d9d9",
  labels: "#737373",
  tooltipBackground: "#212529",
};

// Always show the full calendar month on the axis, regardless of how much usage data has been
// returned (e.g. a newly-enabled subscription, a month with partial usage, or no usage at all),
// so the chart's shape stays consistent - days with no recorded usage simply show nothing.
const buildMonthScaffold = (monthDate: Date): TokenUsagePoint[] => {
  const daysInMonth = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0).getDate();
  return Array.from({ length: daysInMonth }, (_, i) => {
    const date = new Date(monthDate.getFullYear(), monthDate.getMonth(), i + 1);
    return {
      label: date.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
      day: `${date.getDate()}`,
      tokens: null,
    };
  });
};

export const AiTokenUsageChart = ({
  data,
  monthLabel,
  monthDate,
  usageLimit,
  isCurrentMonth,
  onPrevMonth,
  onNextMonth,
  onCurrentMonth,
  isNextMonthDisabled,
  isLoading,
  error,
}: {
  data: TokenUsagePoint[];
  monthLabel: string;
  monthDate: Date;
  usageLimit?: number | null;
  isCurrentMonth: boolean;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  onCurrentMonth: () => void;
  isNextMonthDisabled?: boolean;
  isLoading?: boolean;
  error?: string | null;
}) => {
  const monthScaffold = useMemo(() => buildMonthScaffold(monthDate), [monthDate]);
  const displayData = useMemo(() => {
    const tokensByDay = new Map(data.map((point) => [point.day, point.tokens]));
    return monthScaffold.map((point) => ({
      ...point,
      tokens: tokensByDay.has(point.day) ? tokensByDay.get(point.day) ?? null : null,
    }));
  }, [monthScaffold, data]);

  const chartOptions = useMemo(() => {
    let runningTotal = 0;
    const cumulativeTotals = displayData.map((point) => {
      runningTotal += point.tokens ?? 0;
      return runningTotal;
    });

    return {
      chart: {
        height: 320,
        backgroundColor: "transparent",
        style: {
          fontFamily: "Inter, sans-serif",
        },
      },
      title: { text: "" },
      credits: { enabled: false },
      legend: {
        enabled: true,
        itemStyle: { color: CHART_COLORS.labels },
      },
      exporting: { enabled: false },
      xAxis: {
        categories: displayData.map((point) => point.label),
        tickInterval: 2,
        lineColor: CHART_COLORS.gridline,
        tickColor: CHART_COLORS.gridline,
        labels: {
          style: { color: CHART_COLORS.labels },
        },
      },
      yAxis: [
        {
          title: { text: "" },
          gridLineColor: CHART_COLORS.gridline,
          labels: {
            style: { color: CHART_COLORS.labels },
            formatter(this: Highcharts.AxisLabelsFormatterContextObject) {
              return tokenFormatter.format(Number(this.value ?? 0));
            },
          },
        },
        {
          title: { text: "" },
          gridLineColor: CHART_COLORS.gridline,
          opposite: true,
          labels: {
            style: { color: CHART_COLORS.labels },
            formatter(this: Highcharts.AxisLabelsFormatterContextObject) {
              return tokenFormatter.format(Number(this.value ?? 0));
            },
          },
          plotLines: usageLimit
            ? [
                {
                  value: usageLimit,
                  color: CHART_COLORS.limitLine,
                  dashStyle: "Solid",
                  width: 1,
                  zIndex: 5,
                  label: {
                    text: `Token limit (${tokenFormatter.format(usageLimit)})`,
                    align: "left",
                    style: { color: CHART_COLORS.limitLine },
                  },
                },
              ]
            : [],
        },
      ],
      tooltip: {
        shared: true,
        backgroundColor: CHART_COLORS.tooltipBackground,
        borderColor: CHART_COLORS.tooltipBackground,
        style: { color: "#ffffff" },
        formatter(this: any) {
          const label = this.x;
          const dailyPoint = this.points?.find((point: any) => point.series.name === "Tokens per day");
          const totalPoint = this.points?.find((point: any) => point.series.name === "Total tokens this month");

          const dailyValue = typeof dailyPoint?.y === "number" ? dailyPoint.y : null;
          const totalValue = typeof totalPoint?.y === "number" ? totalPoint.y : null;

          const dailyText = dailyValue === null
            ? "No usage recorded yet"
            : `${tokenFormatter.format(dailyValue)} tokens`;
          const totalText = totalValue === null
            ? ""
            : `<br/>Total this month: ${tokenFormatter.format(totalValue)} tokens`;

          return `<strong>${label}</strong><br/>${dailyText}${totalText}`;
        },
      },
      plotOptions: {
        column: {
          borderRadius: 2,
          borderWidth: 0,
          pointPadding: 0.1,
          groupPadding: 0.1,
        },
        line: {
          marker: {
            enabled: false,
          },
        },
      },
      series: [
        {
          type: "column",
          name: "Tokens per day",
          yAxis: 0,
          data: displayData.map((point) => point.tokens),
          color: CHART_COLORS.line,
        },
        {
          type: "line",
          name: "Total tokens this month",
          yAxis: 1,
          data: cumulativeTotals,
          color: CHART_COLORS.cumulativeLine,
          dashStyle: "Solid",
          lineWidth: 2,
          states: {
            hover: {
              lineWidth: 2,
            },
          },
        },
      ],
    } satisfies Highcharts.Options;
  }, [displayData, usageLimit]);

  const chartRef = useRef<HighchartsReactRefObject>(null);

  // Highcharts only reflows on its own internal `window.resize` listener, which doesn't fire for
  // container resizes caused by CSS grid/flex layout changes (e.g. toggling the side nav, or the
  // chart's own column shrinking within its grid track) - only on the browser window itself being
  // resized, and even then only after the chart is nudged once. Observing the container directly
  // and reflowing on every size change makes the chart track its own space reliably.
  useEffect(() => {
    const container = chartRef.current?.container.current;
    if (!container) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      chartRef.current?.chart?.reflow();
    });
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, [isLoading, error]);

  return (
    <div className="min-w-0">
      <div className="flex flex-row items-start justify-between gap-4">
        <AiSectionHeading title="Token usage" subtitle={`Daily token usage for ${monthLabel}.`} />
        <div className="flex items-center gap-1 pt-2 pr-8">
          <Button variant="outline" size="icon" onClick={onPrevMonth} aria-label="Previous month">
            <FontAwesomeIcon icon={faChevronLeft} />
          </Button>
          <Button variant="outline" size="sm" onClick={onCurrentMonth} disabled={isCurrentMonth}>
            Current month
          </Button>
          <Button variant="outline" size="icon" onClick={onNextMonth} aria-label="Next month" disabled={isNextMonthDisabled}>
            <FontAwesomeIcon icon={faChevronRight} />
          </Button>
        </div>
      </div>
      {error ? (
        <div className="flex h-[320px] items-center justify-center text-sm text-muted-foreground">{error}</div>
      ) : isLoading ? (
        <div className="flex h-[320px] items-center justify-center">
          <Spinner size="lg" />
        </div>
      ) : (
        <HighchartsReact ref={chartRef} highcharts={Highcharts} options={chartOptions} immutable />
      )}
      <hr className="pb-4 mt-4" />
    </div>
  );
};
